import {
    faArrowsRotate,
    faChartLine,
    faCircleCheck,
    faCopy,
    faCreditCard,
    faPaperPlane,
    faReply,
    faFileLines,
    faEnvelope,
    faKey,
    faMagnifyingGlass,
    faScaleBalanced,
    faServer,
    faShieldHalved,
    faTicket,
    faTriangleExclamation,
    faUserCheck,
    faUsers,
    faWallet,
    faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import { useEffect, useState } from 'react';
import Footer from '../Footer';

const tabList = [
    { id: 'overview', label: 'Overview', icon: faChartLine },
    { id: 'users', label: 'Users', icon: faUsers },
    { id: 'financial', label: 'Financial', icon: faWallet },
    { id: 'adjustment', label: 'Adjustment', icon: faPaperPlane },
    { id: 'subscriptions', label: 'Subscriptions', icon: faCreditCard },
    { id: 'support', label: 'Support', icon: faTicket },
    { id: 'email', label: 'Email', icon: faEnvelope },
    { id: 'compliance', label: 'Compliance', icon: faShieldHalved },
    { id: 'audit', label: 'Audit', icon: faScaleBalanced },
    { id: 'system', label: 'System', icon: faServer },
];

const chartColors = ['#257f8f', '#d94d73', '#d9972b', '#3b7dd8', '#4f9a5f', '#7a5cc6'];

const emptyPage = {
    rows: [],
    total: 0,
    page: 0,
    limit: 20,
};

const emptyEmailForm = {
    to: '',
    recipientName: '',
    subject: '',
    html: '',
    text: '',
};

const emptyAdjustmentData = {
    address: '',
    balance: {
        stable: null,
        unstable: null,
        error: '',
    },
    transactions: [],
    limit: 25,
    errors: [],
};

const emptyAdjustmentForm = {
    to: '',
    amount: '',
};

const supportStatusOptions = ['open', 'resolved', 'closed'];
const defaultMaintenanceForm = {
    enabled: false,
    message: 'Pagado is temporarily down for maintenance.',
};

function toNumber(value) {
    const number = Number(value || 0);
    return Number.isFinite(number) ? number : 0;
}

function formatNumber(value) {
    return new Intl.NumberFormat().format(toNumber(value));
}

function formatAmount(value) {
    if (value === null || value === undefined || value === '') {
        return 'n/a';
    }

    return `${formatNumber(value)} MLX`;
}

function formatAmountInput(value) {
    return String(value || '')
        .replace(/[^0-9]/g, '')
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function unsignedIntegerString(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const normalized = String(value).trim();

    if (!/^\d+$/.test(normalized)) {
        return null;
    }

    return normalized.replace(/^0+(?=\d)/, '');
}

function compareUnsignedIntegerStrings(left, right) {
    if (left.length !== right.length) {
        return left.length > right.length ? 1 : -1;
    }

    if (left === right) {
        return 0;
    }

    return left > right ? 1 : -1;
}

function subtractUnsignedIntegerStrings(left, right) {
    let carry = 0;
    let result = '';
    let leftIndex = left.length - 1;
    let rightIndex = right.length - 1;

    while (leftIndex >= 0 || rightIndex >= 0) {
        const leftDigit = leftIndex >= 0 ? Number(left[leftIndex]) : 0;
        const rightDigit = rightIndex >= 0 ? Number(right[rightIndex]) : 0;
        let nextDigit = leftDigit - carry - rightDigit;

        if (nextDigit < 0) {
            nextDigit += 10;
            carry = 1;
        } else {
            carry = 0;
        }

        result = String(nextDigit) + result;
        leftIndex -= 1;
        rightIndex -= 1;
    }

    return result.replace(/^0+(?=\d)/, '');
}

function addUnsignedIntegerStrings(left, right) {
    let carry = 0;
    let result = '';
    let leftIndex = left.length - 1;
    let rightIndex = right.length - 1;

    while (leftIndex >= 0 || rightIndex >= 0 || carry > 0) {
        const leftDigit = leftIndex >= 0 ? Number(left[leftIndex]) : 0;
        const rightDigit = rightIndex >= 0 ? Number(right[rightIndex]) : 0;
        const nextValue = leftDigit + rightDigit + carry;

        result = String(nextValue % 10) + result;
        carry = Math.floor(nextValue / 10);
        leftIndex -= 1;
        rightIndex -= 1;
    }

    return result.replace(/^0+(?=\d)/, '');
}

function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

function amountNeededForMismatch(row) {
    if (!row || row.node_error) {
        return '';
    }

    const databaseBalance = unsignedIntegerString(row.database_balance);
    const nodeStable = unsignedIntegerString(row.node_stable);

    if (databaseBalance === null || nodeStable === null) {
        return '';
    }

    if (compareUnsignedIntegerStrings(databaseBalance, nodeStable) <= 0) {
        return '';
    }

    return subtractUnsignedIntegerStrings(databaseBalance, nodeStable);
}

function signedAmount(row) {
    const sign = row.transaction_type === 'credit' ? '+' : row.transaction_type === 'debit' ? '-' : '';

    return `${sign}${formatAmount(row.amount)}`;
}

function formatDate(value) {
    if (!value) {
        return 'n/a';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return 'n/a';
    }

    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function titleCase(value) {
    if (!value) {
        return 'Unset';
    }

    return String(value)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, letter => letter.toUpperCase());
}

function shortHash(value) {
    if (!value) {
        return 'n/a';
    }

    const stringValue = String(value);
    if (stringValue.length <= 18) {
        return stringValue;
    }

    return `${stringValue.slice(0, 10)}...${stringValue.slice(-6)}`;
}


function requestErrorMessage(err, fallback) {
    const data = err.response?.data;

    if (data && typeof data === 'object') {
        return data.error || data.message || fallback;
    }

    if (typeof data === 'string') {
        const clean = data.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

        if (clean) {
            return `Request failed${err.response?.status ? ` (${err.response.status})` : ''}: ${clean.slice(0, 220)}`;
        }
    }

    if (err.response?.status) {
        return `Request failed with status ${err.response.status}`;
    }

    return err.message || fallback;
}

function statusClass(value) {
    const normalized = String(value || '').toLowerCase();

    if (['active', 'approved', 'resolved', 'credit', 'admin', 'mirrored', 'sent'].includes(normalized)) {
        return 'admin-badge success';
    }

    if (['pending', 'open', 'initiated', 'merchant', 'draft', 'queued'].includes(normalized)) {
        return 'admin-badge warning';
    }

    if ([
        'rejected',
        'closed',
        'expired',
        'debit',
        'canceled',
        'missing_in_database',
        'missing_on_node',
        'amount_mismatch',
        'node_fetch_error',
        'error',
        'failed',
    ].includes(normalized)) {
        return 'admin-badge danger';
    }

    return 'admin-badge neutral';
}

function mirrorStatusLabel(value) {
    const normalized = String(value || '').toLowerCase();

    if (normalized === 'missing_in_database' || normalized === 'missing_on_node') {
        return 'Missing';
    }

    if (normalized === 'amount_mismatch') {
        return 'Mismatch';
    }

    if (normalized === 'node_fetch_error') {
        return 'Error';
    }

    return titleCase(value);
}

function MetricCard({ title, value, detail, icon, tone = 'blue' }) {
    return (
        <div className={`admin-metric ${tone}`}>
            <div className="admin-metric-icon" aria-hidden="true">
                <FontAwesomeIcon icon={icon} />
            </div>
            <div>
                <p>{title}</p>
                <strong>{value}</strong>
                {detail && <span>{detail}</span>}
            </div>
        </div>
    );
}

function GeoIpNotice({ geoIp }) {
    if (!geoIp) {
        return null;
    }

    const status = geoIp.available ? 'Current database available' : 'GeoIP database unavailable';
    const downloadedAt = geoIp.downloaded_at ? formatDate(geoIp.downloaded_at) : 'n/a';
    const buildEpoch = geoIp.build_epoch ? formatDate(geoIp.build_epoch) : 'n/a';

    return (
        <div className={`admin-system-notice ${geoIp.available ? 'success' : 'warning'}`}>
            <div className="admin-system-notice-icon" aria-hidden="true">
                <FontAwesomeIcon icon={faShieldHalved} />
            </div>
            <div>
                <p>{status}</p>
                <strong>GeoIP last downloaded: {downloadedAt}</strong>
                <span>
                    MaxMind build: {buildEpoch}
                    {geoIp.database_type ? ` · ${geoIp.database_type}` : ''}
                    {geoIp.error ? ` · ${geoIp.error}` : ''}
                </span>
            </div>
        </div>
    );
}

function EmptyState({ label = 'No data available' }) {
    return <p className="admin-empty">{label}</p>;
}

function DonutChart({ rows = [], label = 'Total' }) {
    const segments = rows.map((row, index) => ({
        label: titleCase(row.label),
        value: toNumber(row.value),
        color: chartColors[index % chartColors.length],
    }));
    const total = segments.reduce((sum, row) => sum + row.value, 0);
    let cursor = 0;
    const gradient = total > 0
        ? segments.map(row => {
            const start = cursor;
            const end = cursor + ((row.value / total) * 360);
            cursor = end;
            return `${row.color} ${start}deg ${end}deg`;
        }).join(', ')
        : '#dfe5eb 0deg 360deg';

    return (
        <div className="admin-donut-wrap">
            <div className="admin-donut" style={{ background: `conic-gradient(${gradient})` }}>
                <div>
                    <strong>{formatNumber(total)}</strong>
                    <span>{label}</span>
                </div>
            </div>
            <div className="admin-chart-legend">
                {segments.length > 0 ? segments.map(row => (
                    <div key={row.label}>
                        <span style={{ backgroundColor: row.color }}></span>
                        <p>{row.label}</p>
                        <strong>{formatNumber(row.value)}</strong>
                    </div>
                )) : <EmptyState />}
            </div>
        </div>
    );
}

function BarList({ rows = [], valueKey = 'value' }) {
    const max = Math.max(...rows.map(row => toNumber(row[valueKey])), 0);

    if (!rows.length) {
        return <EmptyState />;
    }

    return (
        <div className="admin-bars">
            {rows.map((row, index) => {
                const value = toNumber(row[valueKey]);
                const width = max > 0 ? Math.max((value / max) * 100, 4) : 0;

                return (
                    <div className="admin-bar-row" key={`${row.label}-${index}`}>
                        <div className="admin-bar-label">
                            <span>{titleCase(row.label)}</span>
                            <strong>{formatNumber(value)}</strong>
                        </div>
                        <div className="admin-bar-track">
                            <div
                                className="admin-bar-fill"
                                style={{ width: `${width}%`, backgroundColor: chartColors[index % chartColors.length] }}
                            ></div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function LineChart({ rows = [], valueResolver = row => row.value, label = 'Trend' }) {
    const width = 640;
    const height = 220;
    const padding = 28;
    const points = rows.map(row => ({
        label: row.label,
        value: toNumber(valueResolver(row)),
    }));
    const max = Math.max(...points.map(point => point.value), 1);
    const innerWidth = width - (padding * 2);
    const innerHeight = height - (padding * 2);
    const pathPoints = points.map((point, index) => {
        const x = points.length > 1
            ? padding + ((innerWidth / (points.length - 1)) * index)
            : width / 2;
        const y = height - padding - ((point.value / max) * innerHeight);
        return `${x},${y}`;
    }).join(' ');

    if (!points.length) {
        return <EmptyState />;
    }

    return (
        <div className="admin-line-chart" role="img" aria-label={label}>
            <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
                <line x1={padding} y1={padding} x2={padding} y2={height - padding} />
                <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} />
                <polyline points={pathPoints} />
                {points.map((point, index) => {
                    const x = points.length > 1
                        ? padding + ((innerWidth / (points.length - 1)) * index)
                        : width / 2;
                    const y = height - padding - ((point.value / max) * innerHeight);
                    return <circle key={`${point.label}-${index}`} cx={x} cy={y} r="5" />;
                })}
            </svg>
            <div className="admin-line-meta">
                <span>{formatDate(points[0]?.label).split(',')[0]}</span>
                <strong>{formatNumber(max)}</strong>
                <span>{formatDate(points[points.length - 1]?.label).split(',')[0]}</span>
            </div>
        </div>
    );
}

function Panel({ title, action, children }) {
    return (
        <section className="admin-panel">
            <div className="admin-panel-header">
                <h2>{title}</h2>
                {action}
            </div>
            {children}
        </section>
    );
}

function BalanceStrip({ items }) {
    return (
        <div className="admin-balance-strip">
            {items.map(item => (
                <div className="admin-balance-item" key={item.label}>
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                    {item.detail && <small>{item.detail}</small>}
                </div>
            ))}
        </div>
    );
}

function MaintenanceControl({ status, form, saving, notice, error, onFieldChange, onSubmit }) {
    const enabled = Boolean(status?.enabled);
    const formEnabled = Boolean(form.enabled);
    const updatedBy = status?.updated_by_username || status?.updated_by_email || 'n/a';
    const hasModeChange = enabled !== formEnabled;

    return (
        <Panel
            title="Maintenance mode"
            action={<span className={enabled ? 'admin-badge danger' : 'admin-badge success'}>{enabled ? 'Currently on' : 'Currently off'}</span>}
        >
            {error && <div className="alert alert-danger">{error}</div>}
            {notice && <div className="alert alert-success">{notice}</div>}

            <div className={`admin-maintenance-control ${enabled ? 'enabled' : 'disabled'}`}>
                <div className="admin-maintenance-summary">
                    <div className="admin-maintenance-primary">
                        <span>Current status</span>
                        <h3>{enabled ? 'Maintenance is on' : 'Site is available'}</h3>
                        <p>{enabled ? 'Non-admin users are blocked and active sessions are signed out.' : 'Users can log in and use Pagado normally.'}</p>
                    </div>
                    <div className="admin-maintenance-meta">
                        <div>
                            <span>Last updated</span>
                            <strong>{formatDate(status?.updated_at)}</strong>
                        </div>
                        <div>
                            <span>Updated by</span>
                            <strong>{updatedBy}</strong>
                        </div>
                    </div>
                </div>

                <form className="admin-maintenance-form" onSubmit={onSubmit}>
                    <div className="admin-maintenance-switch-row">
                        <div className="admin-maintenance-copy">
                            <label htmlFor="admin-maintenance-enabled">Maintenance access</label>
                            <p>Admins can still sign in and manage this setting while maintenance mode is on.</p>
                        </div>
                        <label className="admin-maintenance-switch">
                            <input
                                id="admin-maintenance-enabled"
                                type="checkbox"
                                checked={formEnabled}
                                onChange={event => onFieldChange('enabled', event.target.checked)}
                            />
                            <span className="admin-maintenance-switch-track" aria-hidden="true">
                                <span className="admin-maintenance-switch-thumb"></span>
                            </span>
                            <span className="admin-maintenance-switch-text">{formEnabled ? 'On' : 'Off'}</span>
                        </label>
                    </div>

                    <div className="admin-maintenance-message-field">
                        <label className="form-label" htmlFor="admin-maintenance-message">Maintenance message</label>
                        <textarea
                            id="admin-maintenance-message"
                            className="form-control admin-maintenance-message"
                            value={form.message}
                            onChange={event => onFieldChange('message', event.target.value)}
                            rows="4"
                        />
                    </div>

                    <div className="admin-maintenance-actions">
                        {hasModeChange && <span>{formEnabled ? 'Maintenance will be enabled after saving.' : 'Maintenance will be disabled after saving.'}</span>}
                        <button type="submit" className="btn btn-primary" disabled={saving || !form.message.trim()}>
                            <FontAwesomeIcon icon={faCircleCheck} />
                            <span>{saving ? 'Saving...' : 'Save maintenance mode'}</span>
                        </button>
                    </div>
                </form>
            </div>
        </Panel>
    );
}

function DataTable({ columns, rows, emptyLabel, onRowClick, rowClassName }) {
    if (!rows || rows.length === 0) {
        return <EmptyState label={emptyLabel} />;
    }

    return (
        <div className="table-responsive">
            <table className="table admin-table align-middle">
                <thead>
                    <tr>
                        {columns.map(column => <th key={column.key}>{column.label}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, rowIndex) => {
                        const className = [
                            onRowClick ? 'admin-clickable-row' : '',
                            rowClassName ? rowClassName(row) : '',
                        ].filter(Boolean).join(' ');
                        const rowKey = row.row_key
                            || row.id
                            || `${row.transaction_hash || 'row'}-${row.source || ''}-${rowIndex}`;
                        const handleKeyDown = event => {
                            if (!onRowClick) {
                                return;
                            }

                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                onRowClick(row);
                            }
                        };

                        return (
                            <tr
                                key={rowKey}
                                className={className || undefined}
                                onClick={onRowClick ? () => onRowClick(row) : undefined}
                                onKeyDown={handleKeyDown}
                                role={onRowClick ? 'button' : undefined}
                                tabIndex={onRowClick ? 0 : undefined}
                            >
                                {columns.map(column => (
                                    <td key={column.key}>{column.render ? column.render(row) : row[column.key]}</td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function splitLines(value) {
    if (Array.isArray(value)) {
        return value.map(item => String(item || '').trim()).filter(Boolean);
    }

    return String(value || '')
        .split(/\|\||\r?\n/)
        .map(item => item.trim())
        .filter(Boolean);
}

function DetailGrid({ items }) {
    return (
        <div className="admin-detail-grid">
            {items.map(item => (
                <div className="admin-detail-item" key={item.label}>
                    <span>{item.label}</span>
                    <strong>{item.value || 'n/a'}</strong>
                </div>
            ))}
        </div>
    );
}

function UserInformationModal({ user, onClose }) {
    if (!user) {
        return null;
    }

    const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ') || 'n/a';
    const addresses = splitLines(user.addresses || user.address);
    const addressKeys = splitLines(user.address_key_identifiers || user.address_key_identifier);

    const profileItems = [
        { label: 'User ID', value: user.id },
        { label: 'Username', value: user.username },
        { label: 'Name', value: fullName },
        { label: 'Email', value: user.email },
        { label: 'Phone', value: user.phone_number },
        { label: 'Account type', value: titleCase(user.account_type) },
        { label: 'Created', value: formatDate(user.created_at) },
        { label: 'Updated', value: formatDate(user.updated_at) },
    ];

    const activityItems = [
        { label: 'Addresses', value: formatNumber(user.address_count) },
        { label: 'KYC records', value: formatNumber(user.kyc_records) },
        { label: 'KYC status', value: titleCase(user.kyc_status || 'none') },
        { label: 'KYC ID types', value: user.kyc_id_types },
        { label: 'KYC countries', value: user.kyc_countries },
        { label: 'API keys', value: formatNumber(user.api_key_count) },
        { label: 'Ledger transactions', value: formatNumber(user.ledger_transaction_count) },
        { label: 'Latest balance', value: formatAmount(user.latest_balance) },
        { label: 'Credit volume', value: formatAmount(user.ledger_credit_volume) },
        { label: 'Debit volume', value: formatAmount(user.ledger_debit_volume) },
        { label: 'Subscription plans', value: formatNumber(user.subscription_plan_count) },
        { label: 'Subscriptions as customer', value: formatNumber(user.subscriber_subscription_count) },
        { label: 'Customer subscriptions', value: formatNumber(user.customer_subscription_count) },
    ];

    return (
        <div className="admin-modal-backdrop" role="presentation" onClick={onClose}>
            <div className="admin-modal admin-user-modal" role="dialog" aria-modal="true" aria-labelledby="admin-user-modal-title" onClick={event => event.stopPropagation()}>
                <div className="admin-modal-header">
                    <div>
                        <span className="admin-kicker">User information</span>
                        <h2 id="admin-user-modal-title">{user.username || fullName}</h2>
                        <p className="admin-muted">{user.email || 'No email'}</p>
                    </div>
                    <button className="btn btn-outline-pagado btn-sm" onClick={onClose}>Close</button>
                </div>

                <div className="admin-user-modal-body">
                    <section>
                        <h3>Profile</h3>
                        <DetailGrid items={profileItems} />
                    </section>

                    <section>
                        <h3>Account activity</h3>
                        <DetailGrid items={activityItems} />
                    </section>

                    <section>
                        <h3>Millix addresses</h3>
                        {addresses.length > 0 ? (
                            <div className="admin-address-list">
                                {addresses.map((address, index) => (
                                    <div key={address + '-' + index}>
                                        <code>{address}</code>
                                        {addressKeys[index] && <small>{addressKeys[index]}</small>}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <EmptyState label="No Millix addresses" />
                        )}
                    </section>

                    <section>
                        <h3>Latest ledger entry</h3>
                        <DetailGrid items={[
                            { label: 'Transaction', value: user.latest_transaction_hash ? shortHash(user.latest_transaction_hash) : 'n/a' },
                            { label: 'Date', value: formatDate(user.latest_transaction_date) },
                        ]} />
                    </section>

                    <section>
                        <h3>Raw row data</h3>
                        <pre className="admin-json-view compact"><code>{JSON.stringify(user, null, 2)}</code></pre>
                    </section>
                </div>
            </div>
        </div>
    );
}



function SupportTicketModal({ detail, reply, saving, notice, error, onClose, onFieldChange, onStatusSave, onReplySubmit }) {
    if (!detail.open) {
        return null;
    }

    const ticket = detail.ticket || {};
    const responses = ticket.responses || [];
    const userLabel = ticket.username || ticket.email || 'n/a';

    return (
        <div className="admin-modal-backdrop" role="presentation" onClick={onClose}>
            <div className="admin-modal admin-support-modal" role="dialog" aria-modal="true" aria-labelledby="admin-support-modal-title" onClick={event => event.stopPropagation()}>
                <div className="admin-modal-header">
                    <div>
                        <span className="admin-kicker">Support ticket</span>
                        <h2 id="admin-support-modal-title">{ticket.id ? `#${ticket.id} ${ticket.subject}` : 'Loading ticket...'}</h2>
                        {ticket.status && <p className="admin-muted"><span className={statusClass(ticket.status)}>{titleCase(ticket.status)}</span></p>}
                    </div>
                    <button className="btn btn-outline-pagado btn-sm" onClick={onClose} disabled={saving}>Close</button>
                </div>

                {detail.error && <div className="alert alert-danger">{detail.error}</div>}
                {error && <div className="alert alert-danger">{error}</div>}
                {notice && <div className="alert alert-success">{notice}</div>}

                {detail.loading ? (
                    <EmptyState label="Loading support ticket..." />
                ) : (
                    <div className="admin-support-modal-body">
                        <section>
                            <DetailGrid items={[
                                { label: 'Ticket', value: ticket.id ? `#${ticket.id}` : 'n/a' },
                                { label: 'Type', value: titleCase(ticket.support_type) },
                                { label: 'User', value: userLabel },
                                { label: 'Email', value: ticket.email },
                                { label: 'Created', value: formatDate(ticket.created_at) },
                                { label: 'Updated', value: formatDate(ticket.updated_at) },
                            ]} />
                            <div className="admin-ticket-description">
                                <span>Message</span>
                                <p>{ticket.description || 'No message'}</p>
                            </div>
                        </section>

                        <section>
                            <h3>Conversation</h3>
                            {responses.length > 0 ? (
                                <div className="admin-support-thread">
                                    {responses.map(response => (
                                        <div className={`admin-support-response ${response.responder_type}`} key={response.id}>
                                            <div>
                                                <strong>{titleCase(response.responder_type)}</strong>
                                                <span>{formatDate(response.created_at)}</span>
                                            </div>
                                            <p>{response.message}</p>
                                            <small>
                                                {response.responder_type === 'admin'
                                                    ? response.emailed ? 'Emailed to user' : response.email_error ? `Email failed: ${response.email_error}` : 'Not emailed to user'
                                                    : response.emailed ? 'Notified support' : response.email_error ? `Support notification failed: ${response.email_error}` : 'User reply'}
                                            </small>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <EmptyState label="No replies yet" />
                            )}
                        </section>

                        <form className="admin-email-form admin-support-reply-form" onSubmit={onReplySubmit}>
                            <div className="admin-email-form-grid">
                                <div className="form-group boxed">
                                    <label className="form-label" htmlFor="admin-support-status">Status</label>
                                    <select
                                        id="admin-support-status"
                                        className="form-control custom-select"
                                        value={reply.status}
                                        onChange={event => onFieldChange('status', event.target.value)}
                                    >
                                        {supportStatusOptions.map(status => <option key={status} value={status}>{titleCase(status)}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="form-group boxed">
                                <label className="form-label" htmlFor="admin-support-reply">Reply</label>
                                <textarea
                                    id="admin-support-reply"
                                    className="form-control admin-email-textarea compact"
                                    value={reply.message}
                                    onChange={event => onFieldChange('message', event.target.value)}
                                    rows="5"
                                    placeholder="Write a support reply"
                                />
                            </div>

                            <div className="admin-email-modal-actions">
                                <button type="button" className="btn btn-outline-pagado" onClick={onStatusSave} disabled={saving || !ticket.id}>
                                    <FontAwesomeIcon icon={faCircleCheck} />
                                    <span>{saving ? 'Saving...' : 'Update status'}</span>
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={saving || !reply.message.trim() || !ticket.id}>
                                    <FontAwesomeIcon icon={faPaperPlane} />
                                    <span>{saving ? 'Sending...' : 'Send reply'}</span>
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
}

function EmailComposerModal({ composer, onClose, onFieldChange, onSubmit, sending }) {
    if (!composer.open) {
        return null;
    }

    const form = composer.form || emptyEmailForm;
    const templateName = composer.template?.name || 'Custom email';

    return (
        <div className="admin-modal-backdrop" role="presentation" onClick={onClose}>
            <div className="admin-modal admin-email-modal" role="dialog" aria-modal="true" aria-labelledby="admin-email-modal-title" onClick={event => event.stopPropagation()}>
                <div className="admin-modal-header">
                    <div>
                        <span className="admin-kicker">Email</span>
                        <h2 id="admin-email-modal-title">{templateName}</h2>
                    </div>
                    <button className="btn btn-outline-pagado btn-sm" onClick={onClose} disabled={sending}>Close</button>
                </div>

                <form className="admin-email-form" onSubmit={onSubmit}>
                    <div className="admin-email-form-grid">
                        <div className="form-group boxed">
                            <label className="form-label" htmlFor="admin-email-to">Recipient email</label>
                            <input
                                id="admin-email-to"
                                className="form-control"
                                type="email"
                                value={form.to}
                                onChange={event => onFieldChange('to', event.target.value)}
                                required
                            />
                        </div>
                        <div className="form-group boxed">
                            <label className="form-label" htmlFor="admin-email-name">Recipient name</label>
                            <input
                                id="admin-email-name"
                                className="form-control"
                                type="text"
                                value={form.recipientName}
                                onChange={event => onFieldChange('recipientName', event.target.value)}
                            />
                        </div>
                    </div>

                    <div className="form-group boxed">
                        <label className="form-label" htmlFor="admin-email-subject">Subject</label>
                        <input
                            id="admin-email-subject"
                            className="form-control"
                            type="text"
                            value={form.subject}
                            onChange={event => onFieldChange('subject', event.target.value)}
                            required
                        />
                    </div>

                    <div className="form-group boxed">
                        <label className="form-label" htmlFor="admin-email-html">HTML</label>
                        <textarea
                            id="admin-email-html"
                            className="form-control admin-email-textarea"
                            value={form.html}
                            onChange={event => onFieldChange('html', event.target.value)}
                            rows="7"
                        />
                    </div>

                    <div className="form-group boxed">
                        <label className="form-label" htmlFor="admin-email-text">Plain text</label>
                        <textarea
                            id="admin-email-text"
                            className="form-control admin-email-textarea compact"
                            value={form.text}
                            onChange={event => onFieldChange('text', event.target.value)}
                            rows="5"
                        />
                    </div>

                    <div className="admin-email-modal-actions">
                        <button type="button" className="btn btn-outline-pagado" onClick={onClose} disabled={sending}>Cancel</button>
                        <button type="submit" className="btn btn-primary" disabled={sending}>
                            <FontAwesomeIcon icon={faPaperPlane} />
                            <span>{sending ? 'Sending...' : 'Send email'}</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function AdjustmentMismatchModal({ row, amountNeeded, sending, error, onClose, onSend }) {
    if (!row) {
        return null;
    }

    const canSend = Boolean(amountNeeded) && !row.node_error;

    return (
        <div className="admin-modal-backdrop" role="presentation" onClick={onClose}>
            <div className="admin-modal admin-adjustment-mismatch-modal" role="dialog" aria-modal="true" aria-labelledby="admin-adjustment-mismatch-title" onClick={event => event.stopPropagation()}>
                <div className="admin-modal-header">
                    <div>
                        <span className="admin-kicker">Balance adjustment</span>
                        <h2 id="admin-adjustment-mismatch-title">{row.username || 'n/a'}</h2>
                        <p className="admin-muted">{row.email || 'No email'}</p>
                    </div>
                    <button className="btn btn-outline-pagado btn-sm" type="button" onClick={onClose} disabled={sending}>Close</button>
                </div>

                {error && <div className="alert alert-danger">{error}</div>}
                {!canSend && (
                    <div className="alert alert-warning">
                        {row.node_error || 'This row does not need a positive send from the adjustment address.'}
                    </div>
                )}

                <DetailGrid items={[
                    { label: 'Address', value: row.address },
                    { label: 'Amount needed', value: amountNeeded ? formatAmount(amountNeeded) : 'n/a' },
                    { label: 'DB latest', value: formatAmount(row.database_balance) },
                    { label: 'DB calculated', value: formatAmount(row.calculated_balance) },
                    { label: 'Node stable', value: formatAmount(row.node_stable) },
                    { label: 'Node unstable', value: formatAmount(row.node_unstable) },
                ]} />

                <div className="admin-adjustment-mismatch-actions">
                    <button className="btn btn-outline-pagado" type="button" onClick={onClose} disabled={sending}>Cancel</button>
                    <button className="btn btn-primary" type="button" onClick={onSend} disabled={sending || !canSend}>
                        <FontAwesomeIcon icon={faPaperPlane} />
                        <span>{sending ? 'Sending...' : 'Send'}</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

function AdminDashboard({ initialTab = 'overview' }) {
    const [activeTab, setActiveTab] = useState(initialTab);
    const [dashboard, setDashboard] = useState(null);
    const [tables, setTables] = useState({
        users: emptyPage,
        transactions: emptyPage,
        support: emptyPage,
        kyc: emptyPage,
        system: { errors: emptyPage, apiKeys: [] },
    });
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [usersLoading, setUsersLoading] = useState(false);
    const [userSearch, setUserSearch] = useState('');
    const [error, setError] = useState('');
    const [forbidden, setForbidden] = useState(false);
    const [audit, setAudit] = useState({
        loading: false,
        error: '',
        data: null,
    });
    const [auditDetail, setAuditDetail] = useState({
        loading: false,
        error: '',
        data: null,
        selected: null,
    });
    const [userDetail, setUserDetail] = useState(null);
    const [transactionDetail, setTransactionDetail] = useState({
        open: false,
        loading: false,
        error: '',
        row: null,
        data: null,
    });
    const [supportDetail, setSupportDetail] = useState({
        open: false,
        loading: false,
        error: '',
        ticket: null,
    });
    const [supportReply, setSupportReply] = useState({
        message: '',
        status: 'open',
    });
    const [supportSaving, setSupportSaving] = useState(false);
    const [supportNotice, setSupportNotice] = useState('');
    const [supportActionError, setSupportActionError] = useState('');
    const [emailDashboard, setEmailDashboard] = useState(null);
    const [emailLoading, setEmailLoading] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [emailSuccess, setEmailSuccess] = useState('');
    const [emailSending, setEmailSending] = useState(false);
    const [emailComposer, setEmailComposer] = useState({
        open: false,
        template: null,
        form: emptyEmailForm,
    });
    const [adjustment, setAdjustment] = useState({
        loading: false,
        sending: false,
        error: '',
        notice: '',
        data: null,
        form: emptyAdjustmentForm,
    });
    const [adjustmentAudit, setAdjustmentAudit] = useState({
        loading: false,
        sending: false,
        error: '',
        data: null,
        selected: null,
    });
    const [adjustmentProcess, setAdjustmentProcess] = useState({
        processing: false,
        current: 0,
        total: 0,
        available: '',
        results: [],
    });
    const [maintenanceForm, setMaintenanceForm] = useState(defaultMaintenanceForm);
    const [maintenanceSaving, setMaintenanceSaving] = useState(false);
    const [maintenanceNotice, setMaintenanceNotice] = useState('');
    const [maintenanceError, setMaintenanceError] = useState('');
    const [lastUpdated, setLastUpdated] = useState(null);

    const metrics = dashboard?.metrics || {};
    const charts = dashboard?.charts || {};
    const recent = dashboard?.recent || {};
    const emailMetrics = emailDashboard?.metrics || {};
    const emailCharts = emailDashboard?.charts || {};
    const emailTemplates = emailDashboard?.templates || [];
    const emailRecentSends = emailDashboard?.recentSends || [];
    const brevoTemplates = emailDashboard?.provider?.brevoTemplates || [];
    const brevoError = emailDashboard?.provider?.brevoError || '';
    const adjustmentData = adjustment.data || emptyAdjustmentData;

    const riskItems = [
        {
            label: 'Pending transactions',
            value: metrics.pendingTransactions,
            detail: `${formatNumber(metrics.pendingVolume)} MLX pending`,
            tone: metrics.pendingTransactions > 0 ? 'warning' : 'success',
        },
        {
            label: 'Open support tickets',
            value: metrics.openTickets,
            detail: `${formatNumber(metrics.totalTickets)} total tickets`,
            tone: metrics.openTickets > 0 ? 'warning' : 'success',
        },
        {
            label: 'Pending KYC',
            value: metrics.pendingKyc,
            detail: `${formatNumber(metrics.totalKyc)} KYC records`,
            tone: metrics.pendingKyc > 0 ? 'warning' : 'success',
        },
        {
            label: 'Errors in 24h',
            value: metrics.errors24h,
            detail: `${formatNumber(metrics.totalErrors)} stored errors`,
            tone: metrics.errors24h > 0 ? 'danger' : 'success',
        },
        {
            label: 'Users missing address',
            value: metrics.usersMissingAddress,
            detail: `${formatNumber(metrics.usersWithAddresses)} users with addresses`,
            tone: metrics.usersMissingAddress > 0 ? 'danger' : 'success',
        },
    ];

    const loadUsers = async (search = userSearch) => {
        setUsersLoading(true);

        try {
            const params = new URLSearchParams({ limit: '12' });
            const trimmedSearch = search.trim();

            if (trimmedSearch) {
                params.set('search', trimmedSearch);
            }

            const usersRes = await axios.get(`${process.env.REACT_APP_SERVER}/api/admin/users?${params.toString()}`);
            setTables(current => ({
                ...current,
                users: usersRes.data.content,
            }));
        } catch (err) {
            setError('Unable to load users. Please try again.');
        } finally {
            setUsersLoading(false);
        }
    };

    const loadData = async () => {
        setRefreshing(true);
        setError('');
        setForbidden(false);
        const userParams = new URLSearchParams({ limit: '12' });

        if (userSearch.trim()) {
            userParams.set('search', userSearch.trim());
        }

        try {
            const [dashboardRes, usersRes, transactionsRes, supportRes, kycRes, systemRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_SERVER}/api/admin/dashboard`),
                axios.get(`${process.env.REACT_APP_SERVER}/api/admin/users?${userParams.toString()}`),
                axios.get(`${process.env.REACT_APP_SERVER}/api/admin/transactions?limit=12`),
                axios.get(`${process.env.REACT_APP_SERVER}/api/admin/support?limit=12`),
                axios.get(`${process.env.REACT_APP_SERVER}/api/admin/kyc?limit=12`),
                axios.get(`${process.env.REACT_APP_SERVER}/api/admin/system?limit=12`),
            ]);

            const systemContent = systemRes.data.content;
            setDashboard(dashboardRes.data.content);
            setMaintenanceForm({
                enabled: Boolean(systemContent.maintenance?.enabled),
                message: systemContent.maintenance?.message || defaultMaintenanceForm.message,
            });
            setTables({
                users: usersRes.data.content,
                transactions: transactionsRes.data.content,
                support: supportRes.data.content,
                kyc: kycRes.data.content,
                system: systemContent,
            });
            setLastUpdated(new Date());
        } catch (err) {
            if (err.response?.status === 403) {
                setForbidden(true);
            } else {
                setError('Unable to load administrator data. Please try again.');
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };




    const loadAdjustment = async (force = false) => {
        if (!force && (adjustment.loading || adjustment.data)) {
            return;
        }

        setAdjustment(current => ({
            ...current,
            loading: true,
            error: '',
        }));

        try {
            const res = await axios.get(`${process.env.REACT_APP_SERVER}/api/admin/adjustment?limit=25`);
            setAdjustment(current => ({
                ...current,
                loading: false,
                error: '',
                data: res.data.content,
            }));
        } catch (err) {
            if (err.response?.status === 403) {
                setForbidden(true);
            }
            setAdjustment(current => ({
                ...current,
                loading: false,
                error: requestErrorMessage(err, 'Unable to load adjustment data. Please try again.'),
            }));
        }
    };

    const loadAdjustmentAudit = async (force = false) => {
        if (!force && (adjustmentAudit.loading || adjustmentAudit.data)) {
            return;
        }

        setAdjustmentAudit(current => ({
            ...current,
            loading: true,
            error: '',
            selected: force ? null : current.selected,
        }));

        try {
            const res = await axios.get(`${process.env.REACT_APP_SERVER}/api/admin/audit`);
            setAdjustmentAudit(current => ({
                ...current,
                loading: false,
                error: '',
                data: res.data.content,
            }));
        } catch (err) {
            if (err.response?.status === 403) {
                setForbidden(true);
            }
            setAdjustmentAudit(current => ({
                ...current,
                loading: false,
                error: requestErrorMessage(err, 'Unable to load balance mismatches. Please try again.'),
                data: null,
            }));
        }
    };

    const refreshAdjustmentPage = async () => {
        await Promise.all([
            loadAdjustment(true),
            loadAdjustmentAudit(true),
        ]);
    };

    const updateAdjustmentForm = (field, value) => {
        setAdjustment(current => ({
            ...current,
            error: '',
            notice: '',
            form: {
                ...current.form,
                [field]: field === 'amount' ? formatAmountInput(value) : value,
            },
        }));
    };

    const copyAdjustmentAddress = () => {
        if (adjustmentData.address && navigator.clipboard) {
            navigator.clipboard.writeText(adjustmentData.address);
        }
    };

    const sendAdjustmentTransaction = async event => {
        event.preventDefault();
        const recipient = adjustment.form.to.trim();
        const amount = adjustment.form.amount.replaceAll(',', '');

        if (!recipient || !amount) {
            setAdjustment(current => ({
                ...current,
                error: 'Recipient and amount are required.',
                notice: '',
            }));
            return;
        }

        const confirmed = window.confirm(`Send ${formatAmount(amount)} from the adjustment address?`);

        if (!confirmed) {
            return;
        }

        setAdjustment(current => ({
            ...current,
            sending: true,
            error: '',
            notice: '',
        }));

        try {
            const res = await axios.post(`${process.env.REACT_APP_SERVER}/api/admin/adjustment/send`, {
                to: recipient,
                amount,
            });
            const transactionHash = res.data.content?.transaction_hash;
            setAdjustment(current => ({
                ...current,
                sending: false,
                form: emptyAdjustmentForm,
                notice: transactionHash ? `Transaction submitted: ${shortHash(transactionHash)}` : 'Transaction submitted.',
            }));
            await Promise.all([
                loadAdjustment(true),
                loadAdjustmentAudit(true),
            ]);
        } catch (err) {
            setAdjustment(current => ({
                ...current,
                sending: false,
                error: requestErrorMessage(err, 'Unable to send adjustment transaction. Please try again.'),
            }));
        }
    };

    const openAdjustmentMismatch = row => {
        setAdjustmentAudit(current => ({
            ...current,
            error: '',
            selected: row,
        }));
    };

    const closeAdjustmentMismatch = () => {
        if (adjustmentAudit.sending) {
            return;
        }

        setAdjustmentAudit(current => ({
            ...current,
            error: '',
            selected: null,
        }));
    };

    const sendAdjustmentMismatch = async () => {
        const row = adjustmentAudit.selected;
        const amountNeeded = amountNeededForMismatch(row);

        if (!row?.address || !amountNeeded) {
            setAdjustmentAudit(current => ({
                ...current,
                error: 'This mismatch does not need a positive adjustment send.',
            }));
            return;
        }

        const confirmed = window.confirm(`Send ${formatAmount(amountNeeded)} from the adjustment address to ${shortHash(row.address)}?`);

        if (!confirmed) {
            return;
        }

        setAdjustment(current => ({
            ...current,
            error: '',
            notice: '',
        }));
        setAdjustmentAudit(current => ({
            ...current,
            sending: true,
            error: '',
        }));

        try {
            const res = await axios.post(`${process.env.REACT_APP_SERVER}/api/admin/adjustment/audit-send`, {
                userId: row.user_id,
                addressId: row.address_id,
                address: row.address,
            });
            const transactionHash = res.data.content?.transaction_hash;
            setAdjustmentAudit(current => ({
                ...current,
                sending: false,
                error: '',
                selected: null,
            }));
            setAdjustment(current => ({
                ...current,
                notice: transactionHash ? `Adjustment submitted: ${shortHash(transactionHash)}` : 'Adjustment submitted.',
            }));
            await Promise.all([
                loadAdjustment(true),
                loadAdjustmentAudit(true),
            ]);
        } catch (err) {
            setAdjustmentAudit(current => ({
                ...current,
                sending: false,
                error: requestErrorMessage(err, 'Unable to send adjustment transaction. Please try again.'),
            }));
        }
    };

    const processAllAdjustments = async () => {
        const rows = adjustmentAuditRows;

        if (!rows.length || adjustmentProcess.processing) {
            return;
        }

        const confirmed = window.confirm(`Process ${formatNumber(rows.length)} balance mismatches from the adjustment address?`);

        if (!confirmed) {
            return;
        }

        const feeAmount = '1000';
        let available = unsignedIntegerString(adjustmentData.balance?.stable) || '0';
        const results = [];
        const stats = {
            sent: 0,
            skipped: 0,
            failed: 0,
        };
        const pushResult = result => {
            results.push(result);
            setAdjustmentProcess(current => ({
                ...current,
                available,
                results: results.slice(-8),
            }));
        };

        setAdjustment(current => ({
            ...current,
            error: '',
            notice: '',
        }));
        setAdjustmentAudit(current => ({
            ...current,
            error: '',
        }));
        setAdjustmentProcess({
            processing: true,
            current: 0,
            total: rows.length,
            available,
            results: [],
        });

        for (let index = 0; index < rows.length; index += 1) {
            const row = rows[index];
            const amountNeeded = amountNeededForMismatch(row);
            const label = row.username || row.email || shortHash(row.address);

            setAdjustmentProcess({
                processing: true,
                current: index + 1,
                total: rows.length,
                available,
                results: results.slice(-8),
            });

            if (!row?.address || !amountNeeded) {
                stats.skipped += 1;
                pushResult({
                    status: 'skipped',
                    label,
                    detail: row.node_error || 'No positive adjustment amount',
                });
                await sleep(20000);
                continue;
            }

            const totalRequired = addUnsignedIntegerStrings(amountNeeded, feeAmount);

            if (compareUnsignedIntegerStrings(totalRequired, available) > 0) {
                stats.skipped += 1;
                pushResult({
                    status: 'skipped',
                    label,
                    detail: `Needs ${formatAmount(totalRequired)}, available ${formatAmount(available)}`,
                });
                await sleep(20000);
                continue;
            }

            try {
                const res = await axios.post(`${process.env.REACT_APP_SERVER}/api/admin/adjustment/audit-send`, {
                    userId: row.user_id,
                    addressId: row.address_id,
                    address: row.address,
                });
                const sentAmount = unsignedIntegerString(res.data.content?.amount) || amountNeeded;
                const sentFee = unsignedIntegerString(res.data.content?.fee) || feeAmount;
                const totalSpent = addUnsignedIntegerStrings(sentAmount, sentFee);
                const transactionHash = res.data.content?.transaction_hash;

                available = compareUnsignedIntegerStrings(totalSpent, available) >= 0
                    ? '0'
                    : subtractUnsignedIntegerStrings(available, totalSpent);
                stats.sent += 1;
                pushResult({
                    status: 'sent',
                    label,
                    detail: transactionHash ? `Transaction ${shortHash(transactionHash)}` : formatAmount(sentAmount),
                });
            } catch (err) {
                stats.failed += 1;
                pushResult({
                    status: 'failed',
                    label,
                    detail: requestErrorMessage(err, 'Unable to send adjustment transaction.'),
                });
                await sleep(20000);
            }
        }

        setAdjustmentProcess(current => ({
            ...current,
            processing: false,
            current: rows.length,
            available,
            results: results.slice(-8),
        }));
        setAdjustment(current => ({
            ...current,
            notice: `Process all finished: ${formatNumber(stats.sent)} sent, ${formatNumber(stats.skipped)} skipped, ${formatNumber(stats.failed)} failed.`,
        }));
        await Promise.all([
            loadAdjustment(true),
            loadAdjustmentAudit(true),
        ]);
    };

    const updateMaintenanceForm = (field, value) => {
        setMaintenanceNotice('');
        setMaintenanceError('');
        setMaintenanceForm(current => ({
            ...current,
            [field]: value,
        }));
    };

    const saveMaintenanceSettings = async event => {
        event.preventDefault();
        setMaintenanceNotice('');
        setMaintenanceError('');

        if (maintenanceForm.enabled && !tables.system.maintenance?.enabled) {
            const confirmed = window.confirm('Turning maintenance mode on will log out non-admin users and block non-admin logins. Continue?');

            if (!confirmed) {
                return;
            }
        }

        setMaintenanceSaving(true);

        try {
            const res = await axios.patch(`${process.env.REACT_APP_SERVER}/api/admin/maintenance`, maintenanceForm);
            const maintenance = res.data.content;
            setMaintenanceForm({
                enabled: Boolean(maintenance.enabled),
                message: maintenance.message || defaultMaintenanceForm.message,
            });
            setTables(current => ({
                ...current,
                system: {
                    ...current.system,
                    maintenance,
                },
            }));
            setMaintenanceNotice('Maintenance settings saved.');
        } catch (err) {
            setMaintenanceError(requestErrorMessage(err, 'Unable to save maintenance settings.'));
        } finally {
            setMaintenanceSaving(false);
        }
    };


    const loadEmailData = async () => {
        setEmailLoading(true);
        setEmailError('');

        try {
            const res = await axios.get(`${process.env.REACT_APP_SERVER}/api/admin/email/dashboard`);
            setEmailDashboard(res.data.content);
        } catch (err) {
            if (err.response?.status === 403) {
                setForbidden(true);
            } else {
                setEmailError('Unable to load email dashboard. Please try again.');
            }
        } finally {
            setEmailLoading(false);
        }
    };

    const openEmailComposer = (template = null) => {
        setEmailError('');
        setEmailSuccess('');
        setEmailComposer({
            open: true,
            template,
            form: {
                to: '',
                recipientName: '',
                subject: template?.subject || '',
                html: template?.html_content || '',
                text: template?.text_content || '',
            },
        });
    };

    const closeEmailComposer = () => {
        if (emailSending) {
            return;
        }

        setEmailComposer({
            open: false,
            template: null,
            form: emptyEmailForm,
        });
    };

    const updateEmailForm = (field, value) => {
        setEmailComposer(current => ({
            ...current,
            form: {
                ...current.form,
                [field]: value,
            },
        }));
    };

    const sendEmail = async event => {
        event.preventDefault();
        setEmailSending(true);
        setEmailError('');
        setEmailSuccess('');

        try {
            await axios.post(`${process.env.REACT_APP_SERVER}/api/admin/email/send`, {
                templateId: emailComposer.template?.id || null,
                to: emailComposer.form.to,
                recipientName: emailComposer.form.recipientName,
                subject: emailComposer.form.subject,
                html: emailComposer.form.html,
                text: emailComposer.form.text,
            });
            setEmailSuccess('Email sent.');
            setEmailComposer({
                open: false,
                template: null,
                form: emptyEmailForm,
            });
            await loadEmailData();
        } catch (err) {
            setEmailError(requestErrorMessage(err, 'Unable to send email. Please try again.'));
        } finally {
            setEmailSending(false);
        }
    };


    const openSupportTicket = async row => {
        if (!row?.id) {
            return;
        }

        setSupportDetail({
            open: true,
            loading: true,
            error: '',
            ticket: row,
        });
        setSupportReply({
            message: '',
            status: row.status || 'open',
        });
        setSupportNotice('');
        setSupportActionError('');

        try {
            const res = await axios.get(`${process.env.REACT_APP_SERVER}/api/admin/support/${row.id}`);
            const ticket = res.data.content;
            setSupportDetail({
                open: true,
                loading: false,
                error: '',
                ticket,
            });
            setSupportReply(current => ({
                ...current,
                status: ticket.status || current.status || 'open',
            }));
        } catch (err) {
            setSupportDetail({
                open: true,
                loading: false,
                error: requestErrorMessage(err, 'Unable to load support ticket. Please try again.'),
                ticket: row,
            });
        }
    };

    const closeSupportTicket = () => {
        if (supportSaving) {
            return;
        }

        setSupportDetail({
            open: false,
            loading: false,
            error: '',
            ticket: null,
        });
        setSupportReply({
            message: '',
            status: 'open',
        });
        setSupportNotice('');
        setSupportActionError('');
    };

    const updateSupportReplyForm = (field, value) => {
        setSupportReply(current => ({
            ...current,
            [field]: value,
        }));
    };

    const applySupportTicket = ticket => {
        setSupportDetail({
            open: true,
            loading: false,
            error: '',
            ticket,
        });
        setSupportReply(current => ({
            ...current,
            status: ticket.status || current.status || 'open',
        }));
    };

    const saveSupportStatus = async () => {
        const ticketId = supportDetail.ticket?.id;

        if (!ticketId) {
            return;
        }

        setSupportSaving(true);
        setSupportNotice('');
        setSupportActionError('');

        try {
            const res = await axios.patch(`${process.env.REACT_APP_SERVER}/api/admin/support/${ticketId}`, {
                status: supportReply.status,
            });
            applySupportTicket(res.data.content);
            setSupportNotice('Support ticket updated.');
            await loadData();
        } catch (err) {
            setSupportActionError(requestErrorMessage(err, 'Unable to update support ticket. Please try again.'));
        } finally {
            setSupportSaving(false);
        }
    };

    const sendSupportReply = async event => {
        event.preventDefault();

        const ticketId = supportDetail.ticket?.id;
        const message = supportReply.message.trim();

        if (!ticketId || !message) {
            return;
        }

        setSupportSaving(true);
        setSupportNotice('');
        setSupportActionError('');

        try {
            const res = await axios.post(`${process.env.REACT_APP_SERVER}/api/admin/support/${ticketId}/reply`, {
                message,
                status: supportReply.status,
            });
            const updatedTicket = res.data.content;
            applySupportTicket(updatedTicket);
            setSupportReply(current => ({
                ...current,
                message: '',
                status: updatedTicket?.status || current.status,
            }));

            if (updatedTicket?.notification?.sent === false) {
                setSupportNotice(`Reply saved, but email could not be sent: ${updatedTicket.notification.error}`);
            } else {
                setSupportNotice('Support reply sent.');
            }

            await loadData();
        } catch (err) {
            setSupportActionError(requestErrorMessage(err, 'Unable to send support reply. Please try again.'));
        } finally {
            setSupportSaving(false);
        }
    };


    const loadAudit = async (force = false) => {
        if (!force && (audit.loading || audit.data)) {
            return;
        }

        if (force) {
            setAuditDetail({
                loading: false,
                error: '',
                data: null,
                selected: null,
            });
        }

        setAudit(current => ({
            ...current,
            loading: true,
            error: '',
        }));

        try {
            const res = await axios.get(`${process.env.REACT_APP_SERVER}/api/admin/audit`);
            setAudit({
                loading: false,
                error: '',
                data: res.data.content,
            });
        } catch (err) {
            setAudit({
                loading: false,
                error: 'Unable to load balance audit. Please try again.',
                data: null,
            });
        }
    };

    const loadAuditDetail = async (row, mode = 'targeted') => {
        if (!row?.user_id) {
            return;
        }

        setAuditDetail({
            loading: true,
            error: '',
            data: null,
            selected: row,
        });

        try {
            const isFullScan = mode === 'full';
            const params = new URLSearchParams({
                userId: row.user_id,
                limit: '1000',
                scanMode: isFullScan ? 'full' : 'targeted',
                fullScan: isFullScan ? 'true' : 'false',
                nodePageSize: '1000',
                timeoutMs: '60000',
                concurrency: '12',
                scanTimeLimitMs: '60000',
                spentOutputFetchLimit: '1500',
            });

            if (row.address_id) {
                params.set('addressId', row.address_id);
            } else if (row.address) {
                params.set('address', row.address);
            }

            const res = await axios.get(`${process.env.REACT_APP_SERVER}/api/admin/audit/transactions?${params.toString()}`);
            setAuditDetail({
                loading: false,
                error: '',
                data: res.data.content,
                selected: row,
            });
        } catch (err) {
            setAuditDetail({
                loading: false,
                error: 'Unable to load audit transactions. Please try again.',
                data: null,
                selected: row,
            });
        }
    };


    const closeTransactionDetail = () => {
        setTransactionDetail({
            open: false,
            loading: false,
            error: '',
            row: null,
            data: null,
        });
    };

    const loadTransactionDetail = async row => {
        if (!row) {
            return;
        }

        setTransactionDetail({
            open: true,
            loading: Boolean(row.transaction_hash),
            error: '',
            row,
            data: row.transaction_hash ? null : row,
        });

        if (!row.transaction_hash) {
            return;
        }

        try {
            const params = new URLSearchParams({
                transactionHash: row.transaction_hash,
                timeoutMs: '60000',
            });

            if (row.shard_id) {
                params.set('shardId', row.shard_id);
            }

            const res = await axios.get(`${process.env.REACT_APP_SERVER}/api/admin/audit/node-transaction?${params.toString()}`);
            setTransactionDetail({
                open: true,
                loading: false,
                error: '',
                row,
                data: res.data.content?.transaction || res.data.content,
            });
        } catch (err) {
            setTransactionDetail({
                open: true,
                loading: false,
                error: 'Unable to load the full Millix transaction. Showing the table row data instead.',
                row,
                data: row,
            });
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (activeTab !== 'users') {
            return undefined;
        }

        const timeout = setTimeout(() => {
            loadUsers(userSearch);
        }, 300);

        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, userSearch]);

    useEffect(() => {
        if (activeTab === 'email' && !emailDashboard && !emailLoading) {
            loadEmailData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    useEffect(() => {
        if (activeTab === 'audit' && !audit.data && !audit.loading) {
            loadAudit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    useEffect(() => {
        if (activeTab !== 'adjustment') {
            return;
        }

        if (!adjustment.data && !adjustment.loading) {
            loadAdjustment();
        }

        if (!adjustmentAudit.data && !adjustmentAudit.loading) {
            loadAdjustmentAudit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const userColumns = [
        { key: 'id', label: 'ID' },
        { key: 'user', label: 'User', render: row => <><strong>{row.username}</strong><span className="admin-muted d-block">{row.email}</span></> },
        { key: 'name', label: 'Name', render: row => `${row.first_name || ''} ${row.last_name || ''}`.trim() || 'n/a' },
        { key: 'account_type', label: 'Type', render: row => <span className={statusClass(row.account_type)}>{titleCase(row.account_type)}</span> },
        { key: 'kyc_status', label: 'KYC', render: row => <span className={statusClass(row.kyc_status)}>{titleCase(row.kyc_status || 'none')}</span> },
        { key: 'address_count', label: 'Addresses', render: row => formatNumber(row.address_count) },
        { key: 'created_at', label: 'Created', render: row => formatDate(row.created_at) },
    ];

    const transactionColumns = [
        { key: 'transaction_hash', label: 'Hash', render: row => <code>{shortHash(row.transaction_hash)}</code> },
        { key: 'user', label: 'User', render: row => <><strong>{row.username || 'n/a'}</strong><span className="admin-muted d-block">{row.email || 'No email'}</span></> },
        { key: 'transaction_type', label: 'Type', render: row => <span className={statusClass(row.transaction_type)}>{titleCase(row.transaction_type)}</span> },
        { key: 'amount', label: 'Amount', render: row => `${formatNumber(row.amount)} MLX` },
        { key: 'balance', label: 'Balance', render: row => `${formatNumber(row.balance)} MLX` },
        { key: 'transaction_date', label: 'Date', render: row => formatDate(row.transaction_date) },
    ];

    const pendingColumns = [
        { key: 'transaction_hash', label: 'Hash', render: row => <code>{shortHash(row.transaction_hash)}</code> },
        { key: 'user', label: 'User', render: row => row.username || row.email || 'n/a' },
        { key: 'transaction_type', label: 'Type', render: row => <span className={statusClass(row.transaction_type)}>{titleCase(row.transaction_type)}</span> },
        { key: 'amount', label: 'Amount', render: row => `${formatNumber(row.amount)} MLX` },
        { key: 'status', label: 'Status', render: row => <span className={statusClass(row.status)}>{titleCase(row.status)}</span> },
        { key: 'create_date', label: 'Created', render: row => formatDate(row.create_date) },
    ];



    const adjustmentTransactionColumns = [
        { key: 'transaction_hash', label: 'Hash', render: row => <><code>{shortHash(row.transaction_hash)}</code><span className="admin-muted d-block">{titleCase(row.source)}</span></> },
        { key: 'transaction_type', label: 'Type', render: row => <span className={statusClass(row.transaction_type)}>{titleCase(row.transaction_type)}</span> },
        {
            key: 'counterparty',
            label: 'From / To',
            render: row => {
                const fromAdjustment = row.from_address === adjustmentData.address;
                const counterparty = fromAdjustment ? row.to_address : row.from_address;

                return <><span className="admin-muted d-block">{fromAdjustment ? 'To' : 'From'}</span><code>{shortHash(counterparty)}</code></>;
            },
        },
        { key: 'amount', label: 'Amount', render: row => <strong>{signedAmount(row)}</strong> },
        { key: 'balance', label: 'Balance', render: row => row.balance === null || row.balance === undefined ? <span className="admin-muted">n/a</span> : formatAmount(row.balance) },
        { key: 'status', label: 'Status', render: row => row.status ? <span className={statusClass(row.status)}>{titleCase(row.status)}</span> : <span className="admin-muted">validated</span> },
        { key: 'created_at', label: 'Date', render: row => formatDate(row.created_at) },
    ];

    const adjustmentAuditRows = (adjustmentAudit.data?.mismatches || []).filter(row => amountNeededForMismatch(row));
    const adjustmentMismatchAmount = amountNeededForMismatch(adjustmentAudit.selected);
    const adjustmentMismatchColumns = [
        { key: 'user', label: 'User', render: row => <><strong>{row.username || 'n/a'}</strong><span className="admin-muted d-block">{row.email || 'No email'}</span></> },
        { key: 'address', label: 'Address', render: row => <code>{shortHash(row.address)}</code> },
        { key: 'database_balance', label: 'DB latest', render: row => formatAmount(row.database_balance) },
        { key: 'node_stable', label: 'Node stable', render: row => row.node_error ? <span className="admin-muted">n/a</span> : formatAmount(row.node_stable) },
        {
            key: 'amount_needed',
            label: 'Needed',
            render: row => {
                const amountNeeded = amountNeededForMismatch(row);

                if (row.node_error) {
                    return <span className="admin-badge danger">Error</span>;
                }

                return amountNeeded ? <strong>{formatAmount(amountNeeded)}</strong> : <span className="admin-muted">none</span>;
            },
        },
    ];

    const supportColumns = [
        { key: 'subject', label: 'Subject', render: row => <><strong>{row.subject}</strong><span className="admin-muted d-block">{titleCase(row.support_type)}</span></> },
        { key: 'user', label: 'User', render: row => <><strong>{row.username || 'n/a'}</strong><span className="admin-muted d-block">{row.email || 'No email'}</span></> },
        { key: 'status', label: 'Status', render: row => <span className={statusClass(row.status)}>{titleCase(row.status)}</span> },
        { key: 'responses', label: 'Replies', render: row => <><strong>{formatNumber(row.response_count)}</strong><span className="admin-muted d-block">{row.latest_response_at ? formatDate(row.latest_response_at) : 'No replies'}</span></> },
        { key: 'created_at', label: 'Created', render: row => formatDate(row.created_at) },
        {
            key: 'reply',
            label: '',
            render: row => (
                <button
                    type="button"
                    className="btn btn-outline-pagado btn-sm admin-icon-action"
                    onClick={event => {
                        event.stopPropagation();
                        openSupportTicket(row);
                    }}
                    title={`Reply to ${row.subject}`}
                    aria-label={`Reply to ${row.subject}`}
                >
                    <FontAwesomeIcon icon={faReply} />
                </button>
            ),
        },
    ];

    const kycColumns = [
        { key: 'user', label: 'User', render: row => <><strong>{row.username || 'n/a'}</strong><span className="admin-muted d-block">{row.email || 'No email'}</span></> },
        { key: 'id_type', label: 'ID type', render: row => titleCase(row.id_type) },
        { key: 'country', label: 'Country' },
        { key: 'kyc_status', label: 'Status', render: row => <span className={statusClass(row.kyc_status)}>{titleCase(row.kyc_status)}</span> },
        { key: 'updated_at', label: 'Updated', render: row => formatDate(row.updated_at) },
    ];

    const errorColumns = [
        { key: 'url', label: 'URL', render: row => <code>{row.url}</code> },
        { key: 'code', label: 'Code', render: row => row.code || 'n/a' },
        { key: 'message', label: 'Message', render: row => row.message || 'n/a' },
        { key: 'created_at', label: 'Created', render: row => formatDate(row.created_at) },
    ];

    const apiKeyColumns = [
        { key: 'api_key_preview', label: 'Key', render: row => <code>{row.api_key_preview}</code> },
        { key: 'user', label: 'User', render: row => <><strong>{row.username || 'n/a'}</strong><span className="admin-muted d-block">{row.email || 'No email'}</span></> },
        { key: 'created_at', label: 'Created', render: row => formatDate(row.created_at) },
    ];


    const emailTemplateColumns = [
        { key: 'name', label: 'Template', render: row => <><strong>{row.name}</strong><span className="admin-muted d-block">{row.description || row.slug}</span></> },
        { key: 'subject', label: 'Subject', render: row => row.subject || 'n/a' },
        { key: 'status', label: 'Status', render: row => <span className={statusClass(row.status)}>{titleCase(row.status)}</span> },
        { key: 'brevo_template_id', label: 'Brevo', render: row => row.brevo_template_id ? <code>{row.brevo_template_id}</code> : <span className="admin-muted">local</span> },
        {
            key: 'send',
            label: '',
            render: row => (
                <button
                    type="button"
                    className="btn btn-outline-pagado btn-sm admin-icon-action"
                    onClick={() => openEmailComposer(row)}
                    title={`Send ${row.name}`}
                    aria-label={`Send ${row.name}`}
                >
                    <FontAwesomeIcon icon={faPaperPlane} />
                </button>
            ),
        },
    ];

    const brevoTemplateColumns = [
        { key: 'id', label: 'ID', render: row => <code>{row.id}</code> },
        { key: 'name', label: 'Template', render: row => <strong>{row.name || 'n/a'}</strong> },
        { key: 'subject', label: 'Subject', render: row => row.subject || 'n/a' },
        { key: 'isActive', label: 'Status', render: row => <span className={statusClass(row.isActive ? 'active' : 'draft')}>{row.isActive ? 'Active' : 'Draft'}</span> },
        { key: 'modifiedAt', label: 'Updated', render: row => formatDate(row.modifiedAt || row.createdAt) },
    ];

    const emailSendColumns = [
        { key: 'recipient', label: 'Recipient', render: row => <><strong>{row.recipient_email}</strong><span className="admin-muted d-block">{row.recipient_name || 'No name'}</span></> },
        { key: 'subject', label: 'Subject', render: row => <><strong>{row.subject}</strong><span className="admin-muted d-block">{row.template_name || 'Custom email'}</span></> },
        { key: 'status', label: 'Status', render: row => <><span className={statusClass(row.status)}>{titleCase(row.status)}</span>{row.error_message && <span className="admin-muted d-block">{row.error_message}</span>}</> },
        { key: 'provider', label: 'Provider', render: row => titleCase(row.provider) },
        { key: 'created_at', label: 'Created', render: row => formatDate(row.sent_at || row.created_at) },
    ];


    const auditRows = audit.data?.mismatches || [];
    const auditSummary = audit.data?.summary || {};
    const auditColumns = [
        { key: 'user', label: 'User', render: row => <><strong>{row.username || 'n/a'}</strong><span className="admin-muted d-block">{row.email || 'No email'}</span></> },
        { key: 'address', label: 'Address', render: row => <code>{shortHash(row.address)}</code> },
        { key: 'node_stable', label: 'Node stable', render: row => row.node_error ? <span className="admin-muted">n/a</span> : formatAmount(row.node_stable) },
        { key: 'database_balance', label: 'Ledger latest', render: row => formatAmount(row.database_balance) },
        { key: 'calculated_balance', label: 'Calculated', render: row => formatAmount(row.calculated_balance) },
        { key: 'node_minus_database', label: 'Node - Latest', render: row => row.node_error ? <span className="admin-muted">n/a</span> : <strong className={row.node_matches_database ? 'admin-muted' : ''}>{formatAmount(row.node_minus_database)}</strong> },
        { key: 'node_minus_calculated', label: 'Node - Calculated', render: row => row.node_error ? <span className="admin-muted">n/a</span> : <strong className={row.node_matches_calculated ? 'admin-muted' : ''}>{formatAmount(row.node_minus_calculated)}</strong> },
        { key: 'calculated_minus_database', label: 'Calc - Latest', render: row => <strong className={row.calculated_matches_database ? 'admin-muted' : ''}>{formatAmount(row.calculated_minus_database)}</strong> },
        { key: 'node_error', label: 'Node error', render: row => row.node_error || <span className="admin-muted">none</span> },
    ];
    const auditDetailData = auditDetail.data || {};
    const auditDetailSummary = auditDetailData.summary || {};
    const auditDetailScanLabel = auditDetailSummary.nodeFullScan
        ? 'full'
        : auditDetailSummary.nodeScanMode === 'database_hashes_and_spent_output_scan' ? 'targeted' : 'database';
    const nodeScanDetail = `${formatNumber(auditDetailSummary.nodeRawFetches)} raw, ${formatNumber(auditDetailSummary.scannedNodeTransactions)} listed, ${auditDetailScanLabel}${auditDetailSummary.nodeScanTimedOut ? ', timed out' : ''}`;
    const nodeGapDetail = auditDetailSummary.nodeScanTimedOut
        ? 'scan timed out'
        : auditDetailSummary.nodeLimitReached ? 'scan stopped early' : 'not in DB';
    const auditDetailBalances = auditDetailData.balances || {};
    const auditDetailUser = auditDetailData.user || auditDetail.selected || {};
    const auditRowIsSelected = row => auditDetail.selected
        && row.user_id === auditDetail.selected.user_id
        && String(row.address_id || '') === String(auditDetail.selected.address_id || '');
    const mirrorColumns = [
        { key: 'transaction_hash', label: 'Hash', render: row => <code>{shortHash(row.transaction_hash)}</code> },
        { key: 'transaction_type', label: 'Type', render: row => <span className={statusClass(row.transaction_type)}>{titleCase(row.transaction_type)}</span> },
        { key: 'amount', label: 'Amount', render: row => formatAmount(row.amount) },
        { key: 'mirror_status', label: 'Mirror', render: row => <span className={statusClass(row.mirror_status)}>{mirrorStatusLabel(row.mirror_status)}</span> },
        { key: 'created_at', label: 'Date', render: row => formatDate(row.created_at) },
    ];
    const nodeTransactionColumns = [
        ...mirrorColumns,
        { key: 'from_address', label: 'From', render: row => <code>{shortHash(row.from_address)}</code> },
        { key: 'to_address', label: 'To', render: row => <code>{shortHash(row.to_address)}</code> },
    ];
    const databaseTransactionColumns = [
        ...mirrorColumns,
        { key: 'source', label: 'Source', render: row => titleCase(row.source) },
        { key: 'status', label: 'Status', render: row => row.status ? <span className={statusClass(row.status)}>{titleCase(row.status)}</span> : <span className="admin-muted">n/a</span> },
    ];
    const millixBalanceItems = [
        { label: 'Millix stable', value: formatAmount(auditDetailBalances.millix?.stable) },
        { label: 'Millix unstable', value: formatAmount(auditDetailBalances.millix?.unstable), detail: auditDetailBalances.millix?.error || '' },
    ];
    const ledgerBalanceItems = [
        { label: 'Ledger latest', value: formatAmount(auditDetailBalances.ledger?.latest), detail: auditDetailBalances.ledger?.latest_transaction_hash ? `tx ${shortHash(auditDetailBalances.ledger.latest_transaction_hash)}` : '' },
        { label: 'Ledger calculated', value: formatAmount(auditDetailBalances.ledger?.calculated), detail: auditDetailBalances.ledger?.error || '' },
    ];

    if (loading) {
        return (
            <>
                <div id="appCapsule" className="admin-page">
                    <div className="section mt-4 text-center">
                        <h1>Loading administrator dashboard...</h1>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (forbidden) {
        return (
            <>
                <div id="appCapsule" className="admin-page">
                    <div className="section admin-access-state">
                        <FontAwesomeIcon icon={faShieldHalved} />
                        <h1>Administrator access required</h1>
                        <p>Your user account must be marked as admin in the database to view this area.</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <div id="appCapsule" className="admin-page">
                <section className="section admin-hero">
                    <div className="container admin-hero-inner">
                        <div>
                            <span className="admin-kicker">Administrator</span>
                            <h1>Financial command center</h1>
                            <p>{lastUpdated ? `Last refreshed ${formatDate(lastUpdated)}` : 'Operational overview for Pagado'}</p>
                        </div>
                        <div className="admin-hero-actions">
                            <a
                                className="btn btn-light admin-email-link"
                                href="/financial/administrator/email"
                                title="Open email dashboard"
                            >
                                <FontAwesomeIcon icon={faEnvelope} />
                                <span>Email</span>
                            </a>
                            <button
                                className="btn btn-light admin-refresh"
                                onClick={() => activeTab === 'audit' ? loadAudit(true) : activeTab === 'email' ? loadEmailData() : activeTab === 'adjustment' ? loadAdjustment(true) : loadData()}
                                disabled={refreshing || (activeTab === 'email' && emailLoading) || (activeTab === 'adjustment' && adjustment.loading)}
                                title="Refresh dashboard"
                                aria-label="Refresh dashboard"
                            >
                                <FontAwesomeIcon icon={faArrowsRotate} />
                            </button>
                        </div>
                    </div>
                </section>

                <div className="container admin-content">
                    {error && <div className="alert alert-danger">{error}</div>}

                    <div className="admin-metric-grid">
                        <MetricCard title="Users" value={formatNumber(metrics.totalUsers)} detail={`${formatNumber(metrics.newUsers7d)} new in 7d`} icon={faUsers} tone="teal" />
                        <MetricCard title="Ledger volume" value={`${formatNumber(metrics.totalVolume)} MLX`} detail={`${formatNumber(metrics.transactions24h)} tx in 24h`} icon={faWallet} tone="blue" />
                        <MetricCard title="Pending" value={formatNumber(metrics.pendingTransactions)} detail={`${formatNumber(metrics.pendingVolume)} MLX queued`} icon={faTriangleExclamation} tone="amber" />
                        <MetricCard title="API keys" value={formatNumber(metrics.apiKeys)} detail="Direct integrations" icon={faKey} tone="rose" />
                    </div>

                    <div className="admin-tabbar" role="tablist" aria-label="Administrator sections">
                        {tabList.map(tab => (
                            <button
                                key={tab.id}
                                type="button"
                                className={activeTab === tab.id ? 'active' : ''}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                <FontAwesomeIcon icon={tab.icon} />
                                <span>{tab.label}</span>
                            </button>
                        ))}
                    </div>

                    {activeTab === 'overview' && (
                        <div className="admin-grid two">
                            <Panel title="Ledger activity">
                                <LineChart
                                    rows={charts.ledgerActivity || []}
                                    valueResolver={row => toNumber(row.credits) + toNumber(row.debits)}
                                    label="Ledger volume over the last 14 days"
                                />
                            </Panel>
                            <Panel title="User mix">
                                <DonutChart rows={charts.accountTypeBreakdown || []} label="users" />
                            </Panel>
                            <Panel title="Risk queue">
                                <div className="admin-risk-list">
                                    {riskItems.map(item => (
                                        <div className={`admin-risk ${item.tone}`} key={item.label}>
                                            <div>
                                                <strong>{formatNumber(item.value)}</strong>
                                                <span>{item.label}</span>
                                            </div>
                                            <p>{item.detail}</p>
                                        </div>
                                    ))}
                                </div>
                            </Panel>
                            <Panel title="Recent users">
                                <DataTable columns={userColumns.slice(1, 5)} rows={recent.users || []} emptyLabel="No users yet" />
                            </Panel>
                        </div>
                    )}

                    {activeTab === 'users' && (
                        <Panel title={`Users (${formatNumber(tables.users.total)})`}>
                            <div className="admin-searchbar">
                                <label htmlFor="admin-user-search">
                                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                                    <span className="visually-hidden">Search users</span>
                                </label>
                                <input
                                    id="admin-user-search"
                                    className="form-control"
                                    type="search"
                                    placeholder="Search username, name, email, or Millix address"
                                    value={userSearch}
                                    onChange={event => setUserSearch(event.target.value)}
                                />
                                {userSearch && (
                                    <button
                                        type="button"
                                        className="btn btn-light"
                                        onClick={() => setUserSearch('')}
                                        title="Clear search"
                                        aria-label="Clear search"
                                    >
                                        <FontAwesomeIcon icon={faXmark} />
                                    </button>
                                )}
                            </div>
                            {usersLoading && <p className="admin-muted admin-search-status">Searching users...</p>}
                            <DataTable columns={userColumns} rows={tables.users.rows} emptyLabel="No users found" onRowClick={setUserDetail} />
                        </Panel>
                    )}

                    {activeTab === 'financial' && (
                        <div className="admin-grid two">
                            <Panel title="Transaction volume by type">
                                <BarList rows={charts.transactionTypeBreakdown || []} />
                            </Panel>
                            <Panel title="Pending transaction queue">
                                <BarList rows={[
                                    { label: 'new', value: metrics.pendingNew },
                                    { label: 'initiated', value: metrics.pendingInitiated },
                                    { label: 'hibernated', value: metrics.pendingHibernated },
                                ]} />
                            </Panel>
                            <Panel title={`Ledger entries (${formatNumber(tables.transactions.total)})`}>
                                <DataTable columns={transactionColumns} rows={tables.transactions.rows} emptyLabel="No transactions found" />
                            </Panel>
                            <Panel title="Recent pending transactions">
                                <DataTable columns={pendingColumns} rows={recent.pendingTransactions || []} emptyLabel="No pending transactions" />
                            </Panel>
                        </div>
                    )}



                    {activeTab === 'adjustment' && (
                        <div className="admin-grid">
                            {adjustment.error && <div className="alert alert-danger">{adjustment.error}</div>}
                            {adjustment.notice && <div className="alert alert-success">{adjustment.notice}</div>}
                            {adjustmentData.errors?.length > 0 && (
                                <div className="alert alert-warning">
                                    {adjustmentData.errors.slice(0, 3).map((item, index) => (
                                        <div key={`${item.source || 'adjustment'}-${index}`}>{item.message}</div>
                                    ))}
                                </div>
                            )}

                            <Panel
                                title="Adjustment address"
                                action={<button className="btn btn-outline-pagado btn-sm" type="button" onClick={refreshAdjustmentPage} disabled={adjustment.loading || adjustmentAudit.loading}>{adjustment.loading || adjustmentAudit.loading ? 'Refreshing...' : 'Refresh'}</button>}
                            >
                                {adjustment.loading && !adjustment.data ? (
                                    <EmptyState label="Loading adjustment data..." />
                                ) : (
                                    <>
                                        <div className="admin-adjustment-address">
                                            <div>
                                                <span>Source and change address</span>
                                                <code>{adjustmentData.address}</code>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-outline-pagado btn-sm admin-icon-action"
                                                onClick={copyAdjustmentAddress}
                                                disabled={!adjustmentData.address}
                                                title="Copy adjustment address"
                                                aria-label="Copy adjustment address"
                                            >
                                                <FontAwesomeIcon icon={faCopy} />
                                            </button>
                                        </div>

                                        <BalanceStrip items={[
                                            { label: 'Stable balance', value: formatAmount(adjustmentData.balance?.stable) },
                                            { label: 'Unstable balance', value: formatAmount(adjustmentData.balance?.unstable), detail: adjustmentData.balance?.error || '' },
                                        ]} />

                                        <form className="admin-adjustment-send-form" onSubmit={sendAdjustmentTransaction}>
                                            <div className="admin-adjustment-form-grid">
                                                <div className="form-group boxed">
                                                    <label className="form-label" htmlFor="admin-adjustment-to">Recipient</label>
                                                    <input
                                                        id="admin-adjustment-to"
                                                        className="form-control"
                                                        type="text"
                                                        value={adjustment.form.to}
                                                        onChange={event => updateAdjustmentForm('to', event.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="form-group boxed">
                                                    <label className="form-label" htmlFor="admin-adjustment-amount">Amount</label>
                                                    <input
                                                        id="admin-adjustment-amount"
                                                        className="form-control"
                                                        type="text"
                                                        inputMode="numeric"
                                                        value={adjustment.form.amount}
                                                        onChange={event => updateAdjustmentForm('amount', event.target.value)}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="admin-adjustment-actions">
                                                <button className="btn btn-primary" type="submit" disabled={adjustment.sending || adjustmentProcess.processing || !adjustment.form.to.trim() || !adjustment.form.amount}>
                                                    <FontAwesomeIcon icon={faPaperPlane} />
                                                    <span>{adjustment.sending ? 'Sending...' : 'Send'}</span>
                                                </button>
                                            </div>
                                        </form>
                                    </>
                                )}
                            </Panel>

                            <Panel
                                title={`Balance mismatches (${formatNumber(adjustmentAuditRows.length)})`}
                                action={(
                                    <div className="admin-adjustment-panel-actions">
                                        <button className="btn btn-primary btn-sm" type="button" onClick={processAllAdjustments} disabled={adjustmentProcess.processing || adjustmentAudit.loading || !adjustmentAuditRows.length}>
                                            {adjustmentProcess.processing ? 'Processing...' : 'Process all'}
                                        </button>
                                        <button className="btn btn-outline-pagado btn-sm" type="button" onClick={() => loadAdjustmentAudit(true)} disabled={adjustmentAudit.loading || adjustmentProcess.processing}>{adjustmentAudit.loading ? 'Auditing...' : 'Run audit'}</button>
                                    </div>
                                )}
                            >
                                {adjustmentAudit.error && !adjustmentAudit.selected && <div className="alert alert-danger">{adjustmentAudit.error}</div>}
                                {adjustmentAudit.loading && adjustmentAudit.data && <p className="admin-muted admin-search-status">Refreshing mismatches...</p>}
                                {(adjustmentProcess.processing || adjustmentProcess.results.length > 0) && (
                                    <div className="admin-adjustment-process">
                                        <div className="admin-adjustment-process-summary">
                                            <strong>{adjustmentProcess.processing ? `Processing ${formatNumber(adjustmentProcess.current)} of ${formatNumber(adjustmentProcess.total)}` : 'Process all complete'}</strong>
                                            <span>Available: {formatAmount(adjustmentProcess.available)}</span>
                                        </div>
                                        {adjustmentProcess.results.length > 0 && (
                                            <div className="admin-adjustment-process-log">
                                                {adjustmentProcess.results.map((result, index) => (
                                                    <div className={'admin-adjustment-process-row ' + result.status} key={result.status + '-' + result.label + '-' + index}>
                                                        <strong>{titleCase(result.status)}</strong>
                                                        <span>{result.label}</span>
                                                        <small>{result.detail}</small>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {adjustmentAudit.loading && !adjustmentAudit.data ? (
                                    <EmptyState label="Checking Millix node balances..." />
                                ) : (
                                    <div className="admin-adjustment-mismatch-table">
                                        <DataTable
                                            columns={adjustmentMismatchColumns}
                                            rows={adjustmentAuditRows}
                                            emptyLabel="No positive balance mismatches found"
                                            onRowClick={openAdjustmentMismatch}
                                            rowClassName={row => row.node_error ? 'admin-row-danger' : ''}
                                        />
                                    </div>
                                )}
                            </Panel>

                            <Panel title={`Transactions (${formatNumber(adjustmentData.transactions?.length || 0)})`}>
                                {adjustment.loading && adjustment.data && <p className="admin-muted admin-search-status">Refreshing transactions...</p>}
                                <DataTable
                                    columns={adjustmentTransactionColumns}
                                    rows={adjustmentData.transactions || []}
                                    emptyLabel="No adjustment transactions found"
                                    onRowClick={loadTransactionDetail}
                                />
                            </Panel>
                        </div>
                    )}

                    {activeTab === 'subscriptions' && (
                        <div className="admin-grid two">
                            <Panel title="Subscription status">
                                <DonutChart rows={charts.subscriptionStatus || []} label="subscriptions" />
                            </Panel>
                            <Panel title="Subscription operations">
                                <div className="admin-mini-grid">
                                    <MetricCard title="Active" value={formatNumber(metrics.activeSubscriptions)} detail="currently billing" icon={faUserCheck} tone="teal" />
                                    <MetricCard title="Plans" value={formatNumber(metrics.subscriptionPlans)} detail="provider plans" icon={faCreditCard} tone="blue" />
                                    <MetricCard title="Payments" value={formatNumber(metrics.subscriptionPayments)} detail={`${formatNumber(metrics.recordedPlanValue)} plan value`} icon={faWallet} tone="rose" />
                                </div>
                            </Panel>
                        </div>
                    )}

                    {activeTab === 'support' && (
                        <div className="admin-grid two">
                            <Panel title="Support status">
                                <DonutChart rows={charts.supportStatus || []} label="tickets" />
                            </Panel>
                            <Panel title={`Support tickets (${formatNumber(tables.support.total)})`}>
                                <DataTable columns={supportColumns} rows={tables.support.rows} emptyLabel="No support tickets" onRowClick={openSupportTicket} />
                            </Panel>
                        </div>
                    )}

                    {activeTab === 'email' && (
                        <div className="admin-grid">
                            {emailError && <div className="alert alert-danger">{emailError}</div>}
                            {emailSuccess && <div className="alert alert-success">{emailSuccess}</div>}
                            {emailLoading && !emailDashboard && <Panel title="Email dashboard"><EmptyState label="Loading email dashboard..." /></Panel>}
                            {!emailLoading && emailDashboard && !emailDashboard.provider?.brevoEnabled && (
                                <div className="alert alert-warning">Brevo API key is not configured. Email sends will use SMTP fallback if available.</div>
                            )}
                            {brevoError && <div className="alert alert-warning">Brevo templates could not be loaded: {brevoError}</div>}

                            {emailDashboard && (
                                <>
                                    <div className="admin-metric-grid audit">
                                        <MetricCard title="Sends" value={formatNumber(emailMetrics.totalSends)} detail={`${formatNumber(emailMetrics.sends24h)} in 24h`} icon={faPaperPlane} tone="blue" />
                                        <MetricCard title="Success" value={`${formatNumber(emailMetrics.successRate)}%`} detail={`${formatNumber(emailMetrics.sentSends)} sent`} icon={faChartLine} tone="teal" />
                                        <MetricCard title="Errors" value={formatNumber(emailMetrics.failedSends)} detail={`${formatNumber(emailMetrics.failures24h)} in 24h`} icon={faTriangleExclamation} tone="rose" />
                                        <MetricCard title="Templates" value={formatNumber(emailMetrics.totalTemplates)} detail={`${formatNumber(emailMetrics.activeTemplates)} active`} icon={faFileLines} tone="amber" />
                                    </div>

                                    <div className="admin-grid two">
                                        <Panel title="Email activity">
                                            <LineChart
                                                rows={emailCharts.dailySends || []}
                                                valueResolver={row => row.value}
                                                label="Email sends over the last 14 days"
                                            />
                                        </Panel>
                                        <Panel title="Delivery status">
                                            <DonutChart rows={emailCharts.statusBreakdown || []} label="sends" />
                                        </Panel>
                                        <Panel
                                            title={`Email templates (${formatNumber(emailTemplates.length)})`}
                                            action={(
                                                <button className="btn btn-outline-pagado btn-sm" type="button" onClick={() => openEmailComposer()}>
                                                    <FontAwesomeIcon icon={faPaperPlane} />
                                                    <span>New email</span>
                                                </button>
                                            )}
                                        >
                                            <DataTable columns={emailTemplateColumns} rows={emailTemplates} emptyLabel="No email templates" />
                                        </Panel>
                                        <Panel title="Recent sends">
                                            <DataTable columns={emailSendColumns} rows={emailRecentSends} emptyLabel="No email sends recorded" />
                                        </Panel>
                                        {brevoTemplates.length > 0 && (
                                            <Panel title={`Brevo templates (${formatNumber(brevoTemplates.length)})`}>
                                                <DataTable columns={brevoTemplateColumns} rows={brevoTemplates} emptyLabel="No Brevo templates" />
                                            </Panel>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}


                    {activeTab === 'compliance' && (
                        <div className="admin-grid two">
                            <Panel title="KYC status">
                                <DonutChart rows={charts.kycStatus || []} label="records" />
                            </Panel>
                            <Panel title={`KYC records (${formatNumber(tables.kyc.total)})`}>
                                <DataTable columns={kycColumns} rows={tables.kyc.rows} emptyLabel="No KYC records" />
                            </Panel>
                        </div>
                    )}


                    {activeTab === 'audit' && (
                        <div className="admin-grid">
                            <div className="admin-metric-grid audit">
                                <MetricCard title="Checked" value={formatNumber(auditSummary.checked)} detail="user/address rows" icon={faUsers} tone="blue" />
                                <MetricCard title="Matched" value={formatNumber(auditSummary.matched)} detail="all three equal" icon={faUserCheck} tone="teal" />
                                <MetricCard title="Mismatches" value={formatNumber(auditSummary.mismatches)} detail="any balance differs" icon={faScaleBalanced} tone="amber" />
                                <MetricCard title="Calc drift" value={formatNumber(auditSummary.latestCalculatedMismatches)} detail="calculated vs latest" icon={faWallet} tone="blue" />
                                <MetricCard title="Node errors" value={formatNumber(auditSummary.nodeErrors)} detail={`${formatNumber(auditSummary.missingAddresses)} missing address`} icon={faTriangleExclamation} tone="rose" />
                            </div>
                            <Panel
                                title="Node vs latest vs calculated"
                                action={<button className="btn btn-outline-pagado btn-sm" onClick={() => loadAudit(true)} disabled={audit.loading}>{audit.loading ? 'Auditing...' : 'Run audit'}</button>}
                            >
                                {audit.error && <div className="alert alert-danger">{audit.error}</div>}
                                {audit.loading && <EmptyState label="Checking Millix node balances..." />}
                                {!audit.loading && !audit.data && !audit.error && <EmptyState label="Open this tab to run a balance audit." />}
                                {!audit.loading && audit.data && (
                                    <DataTable
                                        columns={auditColumns}
                                        rows={auditRows}
                                        emptyLabel="No balance differences found"
                                        onRowClick={loadAuditDetail}
                                        rowClassName={row => auditRowIsSelected(row) ? 'admin-row-selected' : ''}
                                    />
                                )}
                            </Panel>

                            {auditDetail.selected && (
                                <div className="admin-grid">
                                    <div className="admin-audit-detail-heading">
                                        <div>
                                            <span className="admin-kicker">Selected user</span>
                                            <h2>{auditDetailUser.username || 'n/a'}</h2>
                                            <p>{auditDetailUser.email || 'No email'}</p>
                                        </div>
                                        <div className="admin-audit-detail-actions">
                                            <code>{shortHash(auditDetailUser.address)}</code>
                                            <div>
                                                <button className="btn btn-outline-pagado btn-sm" onClick={() => loadAuditDetail(auditDetail.selected, 'targeted')} disabled={auditDetail.loading}>Refresh</button>
                                                <button className="btn btn-outline-pagado btn-sm" onClick={() => loadAuditDetail(auditDetail.selected, 'full')} disabled={auditDetail.loading}>Full scan</button>
                                            </div>
                                        </div>
                                    </div>

                                    {auditDetail.error && <div className="alert alert-danger">{auditDetail.error}</div>}
                                    {auditDetail.loading && <Panel title="Transaction mirror check"><EmptyState label="Scanning Millix node transactions..." /></Panel>}

                                    {!auditDetail.loading && auditDetail.data && (
                                        <>
                                            {auditDetail.data.errors?.length > 0 && (
                                                <div className="alert alert-warning">
                                                    {auditDetail.data.errors.slice(0, 3).map((item, index) => (
                                                        <div key={`${item.transaction_hash || 'node'}-${index}`}>{item.message}</div>
                                                    ))}
                                                </div>
                                            )}
                                            <div className="admin-metric-grid audit">
                                                <MetricCard title="Node rows" value={formatNumber(auditDetailSummary.nodeTransactions)} detail={nodeScanDetail} icon={faServer} tone="blue" />
                                                <MetricCard title="DB rows" value={formatNumber(auditDetailSummary.databaseTransactions)} detail={`limit ${formatNumber(auditDetailSummary.databaseLimit)}`} icon={faWallet} tone="teal" />
                                                <MetricCard title="Node gaps" value={formatNumber(auditDetailSummary.nodeUnmirrored)} detail={nodeGapDetail} icon={faTriangleExclamation} tone="amber" />
                                                <MetricCard title="DB gaps" value={formatNumber(auditDetailSummary.databaseUnmirrored)} detail="not on node" icon={faScaleBalanced} tone="rose" />
                                            </div>
                                            <div className="admin-grid two">
                                                <Panel title="Millix node transactions">
                                                    <BalanceStrip items={millixBalanceItems} />
                                                    <DataTable
                                                        columns={nodeTransactionColumns}
                                                        rows={auditDetail.data.nodeTransactions || []}
                                                        emptyLabel="No node transactions found"
                                                        onRowClick={loadTransactionDetail}
                                                        rowClassName={row => row.is_mirrored ? '' : 'admin-row-danger'}
                                                    />
                                                </Panel>
                                                <Panel title="Database transactions">
                                                    <BalanceStrip items={ledgerBalanceItems} />
                                                    <DataTable
                                                        columns={databaseTransactionColumns}
                                                        rows={auditDetail.data.databaseTransactions || []}
                                                        emptyLabel="No database transactions found"
                                                        onRowClick={loadTransactionDetail}
                                                        rowClassName={row => row.is_mirrored ? '' : 'admin-row-danger'}
                                                    />
                                                </Panel>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'system' && (
                        <div className="admin-grid">
                            <MaintenanceControl
                                status={tables.system.maintenance}
                                form={maintenanceForm}
                                saving={maintenanceSaving}
                                notice={maintenanceNotice}
                                error={maintenanceError}
                                onFieldChange={updateMaintenanceForm}
                                onSubmit={saveMaintenanceSettings}
                            />
                            <GeoIpNotice geoIp={tables.system.geoIp} />
                            <div className="admin-grid two">
                                <Panel title={`Errors (${formatNumber(tables.system.errors?.total)})`}>
                                    <DataTable columns={errorColumns} rows={tables.system.errors?.rows || []} emptyLabel="No errors recorded" />
                                </Panel>
                                <Panel title="API Direct keys">
                                    <DataTable columns={apiKeyColumns} rows={tables.system.apiKeys || []} emptyLabel="No API keys generated" />
                                </Panel>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <UserInformationModal user={userDetail} onClose={() => setUserDetail(null)} />
            <SupportTicketModal
                detail={supportDetail}
                reply={supportReply}
                saving={supportSaving}
                notice={supportNotice}
                error={supportActionError}
                onClose={closeSupportTicket}
                onFieldChange={updateSupportReplyForm}
                onStatusSave={saveSupportStatus}
                onReplySubmit={sendSupportReply}
            />
            <EmailComposerModal
                composer={emailComposer}
                onClose={closeEmailComposer}
                onFieldChange={updateEmailForm}
                onSubmit={sendEmail}
                sending={emailSending}
            />
            <AdjustmentMismatchModal
                row={adjustmentAudit.selected}
                amountNeeded={adjustmentMismatchAmount}
                sending={adjustmentAudit.sending}
                error={adjustmentAudit.error}
                onClose={closeAdjustmentMismatch}
                onSend={sendAdjustmentMismatch}
            />
            {transactionDetail.open && (
                <div className="admin-modal-backdrop" role="presentation" onClick={closeTransactionDetail}>
                    <div className="admin-modal" role="dialog" aria-modal="true" aria-labelledby="admin-transaction-modal-title" onClick={event => event.stopPropagation()}>
                        <div className="admin-modal-header">
                            <div>
                                <span className="admin-kicker">Transaction</span>
                                <h2 id="admin-transaction-modal-title">{shortHash(transactionDetail.row?.transaction_hash)}</h2>
                            </div>
                            <button className="btn btn-outline-pagado btn-sm" onClick={closeTransactionDetail}>Close</button>
                        </div>
                        {transactionDetail.error && <div className="alert alert-warning">{transactionDetail.error}</div>}
                        {transactionDetail.loading ? (
                            <EmptyState label="Loading full transaction..." />
                        ) : (
                            <pre className="admin-json-view"><code>{JSON.stringify(transactionDetail.data || transactionDetail.row || {}, null, 2)}</code></pre>
                        )}
                    </div>
                </div>
            )}
            <Footer />
        </>
    );
}

export default AdminDashboard;
